import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { BUILDINGS, SPECIAL_EVENTS_ALLOCATION, getContractYearStart } from '../../lib/data';
import { format } from 'date-fns';

export default function Dashboard() {
  const [todaySubmissions, setTodaySubmissions] = useState([]);
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [specialEventHours, setSpecialEventHours] = useState(0);
  const [negativeReports, setNegativeReports] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSub, setExpandedSub] = useState(null);
  const today = new Date();
  const monthName = format(today, 'MMMM yyyy');

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const now = new Date();
    // Local midnight, not a bare date string — `created_at` is timestamptz,
    // and a bare 'yyyy-MM-dd' string gets read as UTC midnight, which for
    // Newfoundland's negative offset is several hours into the previous
    // *local* evening, pulling last night's late submissions into "today".
    const localMidnightIso = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0).toISOString();
    // Same local-midnight reasoning as above, 6 full days back — gives a
    // rolling 7-day window (today included) instead of a bare UTC cutoff.
    const sevenDaysAgoIso = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6, 0, 0, 0).toISOString();
    const monthStart = format(new Date(now.getFullYear(), now.getMonth(), 1), 'yyyy-MM-dd');
    const contractYearStart = format(getContractYearStart(now), 'yyyy-MM-dd');

    const [subsRes, recentRes, eventsRes, reportsRes] = await Promise.all([
      // Today's completed checklists — now includes GPS fields
      supabase
        .from('checklist_submissions')
        .select('id, building_id, worker_name, sign_in, sign_out, gps_lat, gps_lng, gps_accuracy, gps_distance_metres, gps_verified, gps_error, gps_out_lat, gps_out_lng, gps_out_accuracy, gps_out_distance_metres, gps_out_verified, gps_out_error, created_at')
        .gte('created_at', localMidnightIso)
        .order('created_at', { ascending: false }),
      // Past 7 days (includes today) — separate from todaySubmissions so the
      // "Cleaned Today" / pending-buildings stats above stay today-only.
      supabase
        .from('checklist_submissions')
        .select('id, building_id, worker_name, sign_in, sign_out, gps_lat, gps_lng, gps_accuracy, gps_distance_metres, gps_verified, gps_error, gps_out_lat, gps_out_lng, gps_out_accuracy, gps_out_distance_metres, gps_out_verified, gps_out_error, created_at')
        .gte('created_at', sevenDaysAgoIso)
        .order('created_at', { ascending: false }),
      // Special event hours this contract year
      supabase
        .from('special_event_logs')
        .select('hours_worked')
        .gte('event_date', contractYearStart),
      // Negative reports this month
      supabase
        .from('negative_reports')
        .select('id')
        .gte('report_date', monthStart),
    ]);

    const firstErr = subsRes.error || recentRes.error || eventsRes.error || reportsRes.error;
    if (firstErr) {
      // Database messages name columns and constraints — useful in the
      // console, meaningless to the manager reading the banner.
      console.error('Dashboard load failed:', firstErr);
      setError(firstErr.message);
      setLoading(false);
      return;
    }

    setTodaySubmissions(subsRes.data || []);
    setRecentSubmissions(recentRes.data || []);
    setSpecialEventHours(
      (eventsRes.data || []).reduce((sum, e) => sum + (e.hours_worked || 0), 0)
    );
    setNegativeReports((reportsRes.data || []).length);
    setLoading(false);
  }, []);

  useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);

  // Buildings D and L only run Mondays (per their `window` text) — treating
  // every non-Monday as "pending" for them inflated the pending count on
  // 6 of every 7 days for a building that was never actually due.
  function isExpectedToday(building, date) {
    if (building.id === 'D' || building.id === 'L') return date.getDay() === 1;
    return true;
  }

  const activeBuildings = BUILDINGS.filter(b => b.status === 'active');
  const expectedTodayBuildings = activeBuildings.filter(b => isExpectedToday(b, today));
  const cleanedToday = new Set(todaySubmissions.map(s => s.building_id));
  const pendingToday = expectedTodayBuildings.filter(b => !cleanedToday.has(b.id));
  const eventPct = Math.min((specialEventHours / SPECIAL_EVENTS_ALLOCATION) * 100, 100);
  const eventRemaining = Math.max(SPECIAL_EVENTS_ALLOCATION - specialEventHours, 0);
  const currentRevenue = BUILDINGS
    .filter(b => b.status === 'active')
    .reduce((sum, b) => sum + b.monthlyRate, 0);

  // GPS summary stats
  const gpsSubmissions = todaySubmissions.filter(s => s.gps_lat != null);
  const verifiedCount = todaySubmissions.filter(s => s.gps_verified).length;
  const unverifiedCount = gpsSubmissions.filter(s => !s.gps_verified).length;
  const noGpsCount = todaySubmissions.length - gpsSubmissions.length;

  function getGpsBadge(sub) {
    if (sub.gps_verified) {
      // "GPS Verified", not "On-Site" — this badge is shown on closed-out
      // historical rows (Past 7 Days) just as often as live ones, and
      // "On-Site" reads as a present-tense claim that the worker is there
      // right now, which is only ever true for genuinely still-open shifts.
      return { label: 'GPS Verified', color: '#2F7A4F', bg: 'rgba(47,122,79,0.10)', icon: '📍' };
    }
    if (sub.gps_lat != null && !sub.gps_verified) {
      const dist = sub.gps_distance_metres != null ? sub.gps_distance_metres : '?';
      // Small bold badge text — #B5730A fails contrast at this size (same
      // issue already found and fixed on Command Central/Schedule), so the
      // darker #96600A text variant is used here instead.
      return { label: `${dist}m away`, color: '#96600A', bg: 'rgba(181,115,10,0.10)', icon: '⚠️' };
    }
    // Neutral grey for "unknown" — #8B98A0 measures under 3:1 on white at
    // this size, so #5B6B72 is used for the actual readable text instead.
    return { label: 'No GPS', color: '#5B6B72', bg: 'rgba(91,107,114,0.08)', icon: '—' };
  }

  // Checkout GPS is captured separately from check-in (gps_out_* columns)
  // but was never actually surfaced anywhere in the manager app — a worker
  // could check out from home instead of the building and nothing on
  // screen would show it, even though the mismatch was sitting right there
  // in the database. Mirrors getGpsBadge, reading the checkout columns.
  function getCheckoutGpsBadge(sub) {
    if (!sub.sign_out) return null;
    if (sub.gps_out_verified) {
      return { label: 'Checkout Verified', color: '#2F7A4F', bg: 'rgba(47,122,79,0.10)', icon: '📍' };
    }
    if (sub.gps_out_lat != null && !sub.gps_out_verified) {
      const dist = sub.gps_out_distance_metres != null ? sub.gps_out_distance_metres : '?';
      return { label: `Checkout ${dist}m away`, color: '#96600A', bg: 'rgba(181,115,10,0.10)', icon: '⚠️' };
    }
    return { label: 'Checkout: No GPS', color: '#5B6B72', bg: 'rgba(91,107,114,0.08)', icon: '—' };
  }

  function openInMaps(lat, lng) {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
  }

  function renderSubmissionRow(s) {
    const gpsBadge = getGpsBadge(s);
    const checkoutBadge = getCheckoutGpsBadge(s);
    const isExpanded = expandedSub === s.id;
    const building = BUILDINGS.find(b => b.id === s.building_id);
    return (
      <div key={s.id}>
        <div
          style={{ ...styles.subRow, cursor: 'pointer', background: isExpanded ? '#FFFFFF' : 'transparent' }}
          onClick={() => setExpandedSub(isExpanded ? null : s.id)}
        >
          <div style={styles.subBid}>{s.building_id}</div>
          <div style={styles.subName}>{s.worker_name}</div>
          <div style={styles.subTime}>
            {s.sign_in ? format(new Date(s.sign_in), 'h:mm a') : '—'}
            {s.sign_out ? ` → ${format(new Date(s.sign_out), 'h:mm a')}` : ''}
          </div>
          {/* Full check-in/checkout detail (distance, accuracy, map link)
              lives in the expanded panel below — these are just small
              at-a-glance dots, not full badges, so a name next to a time
              range next to two dots comfortably fits one line instead of
              needing two full badges' worth of width. */}
          <div style={styles.dotWrap} title={`Check-in: ${gpsBadge.label}${checkoutBadge ? ` · ${checkoutBadge.label}` : ''}`}>
            <span style={{ ...styles.statusDot, background: gpsBadge.color }} />
            {checkoutBadge && <span style={{ ...styles.statusDot, background: checkoutBadge.color }} />}
          </div>
          <div style={{ color: '#8B98A0', fontSize: 12, flexShrink: 0 }}>{isExpanded ? '▴' : '▾'}</div>
        </div>

        {/* Expanded GPS Detail */}
        {isExpanded && (
          <div style={styles.gpsDetail}>
            <div style={styles.gpsDetailGrid}>
              <div style={styles.gpsDetailItem}>
                <div style={styles.gpsDetailLabel}>Status</div>
                <div style={{ color: gpsBadge.color, fontWeight: 700 }}>
                  {s.gps_verified ? '✓ Verified On-Site' : s.gps_lat ? '⚠ Off-Site' : 'No Location Data'}
                </div>
              </div>

              {s.gps_lat != null && (
                <>
                  <div style={styles.gpsDetailItem}>
                    <div style={styles.gpsDetailLabel}>Distance from Building</div>
                    <div style={{ color: '#1A2B33', fontWeight: 600 }}>
                      {s.gps_distance_metres != null ? `${s.gps_distance_metres} metres` : 'Unknown'}
                    </div>
                  </div>
                  <div style={styles.gpsDetailItem}>
                    <div style={styles.gpsDetailLabel}>GPS Accuracy</div>
                    <div style={{ color: '#1A2B33' }}>
                      ±{s.gps_accuracy || '?'} metres
                    </div>
                  </div>
                  <div style={styles.gpsDetailItem}>
                    <div style={styles.gpsDetailLabel}>Coordinates</div>
                    <div style={{ color: '#5B6B72', fontSize: 12, fontFamily: 'monospace' }}>
                      {s.gps_lat?.toFixed(6)}, {s.gps_lng?.toFixed(6)}
                    </div>
                  </div>
                </>
              )}

              {s.gps_error && (
                <div style={styles.gpsDetailItem}>
                  <div style={styles.gpsDetailLabel}>Reason</div>
                  <div style={{ color: '#5B6B72' }}>{s.gps_error}</div>
                </div>
              )}

              {building && (
                <div style={styles.gpsDetailItem}>
                  <div style={styles.gpsDetailLabel}>Expected Location</div>
                  <div style={{ color: '#5B6B72' }}>{building.name} — {building.address}</div>
                </div>
              )}
            </div>

            {s.gps_lat != null && (
              <button
                style={styles.mapBtn}
                onClick={(e) => { e.stopPropagation(); openInMaps(s.gps_lat, s.gps_lng); }}
              >
                🗺️ Open in Google Maps (Check-in)
              </button>
            )}

            {checkoutBadge && (
              <>
                <div style={styles.gpsDetailDivider} />
                <div style={styles.gpsDetailGrid}>
                  <div style={styles.gpsDetailItem}>
                    <div style={styles.gpsDetailLabel}>Checkout Status</div>
                    <div style={{ color: checkoutBadge.color, fontWeight: 700 }}>
                      {s.gps_out_verified ? '✓ Verified On-Site' : s.gps_out_lat != null ? '⚠ Off-Site' : 'No Location Data'}
                    </div>
                  </div>

                  {s.gps_out_lat != null && (
                    <>
                      <div style={styles.gpsDetailItem}>
                        <div style={styles.gpsDetailLabel}>Distance from Building</div>
                        <div style={{ color: '#1A2B33', fontWeight: 600 }}>
                          {s.gps_out_distance_metres != null ? `${s.gps_out_distance_metres} metres` : 'Unknown'}
                        </div>
                      </div>
                      <div style={styles.gpsDetailItem}>
                        <div style={styles.gpsDetailLabel}>GPS Accuracy</div>
                        <div style={{ color: '#1A2B33' }}>±{s.gps_out_accuracy || '?'} metres</div>
                      </div>
                      <div style={styles.gpsDetailItem}>
                        <div style={styles.gpsDetailLabel}>Coordinates</div>
                        <div style={{ color: '#5B6B72', fontSize: 12, fontFamily: 'monospace' }}>
                          {s.gps_out_lat?.toFixed(6)}, {s.gps_out_lng?.toFixed(6)}
                        </div>
                      </div>
                    </>
                  )}

                  {s.gps_out_error && (
                    <div style={styles.gpsDetailItem}>
                      <div style={styles.gpsDetailLabel}>Reason</div>
                      <div style={{ color: '#5B6B72' }}>{s.gps_out_error}</div>
                    </div>
                  )}
                </div>

                {s.gps_out_lat != null && (
                  <button
                    style={styles.mapBtn}
                    onClick={(e) => { e.stopPropagation(); openInMaps(s.gps_out_lat, s.gps_out_lng); }}
                  >
                    🗺️ Open in Google Maps (Checkout)
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  function groupByDay(submissions) {
    const groups = [];
    const byKey = new Map();
    for (const s of submissions) {
      const d = new Date(s.sign_in || s.created_at);
      const key = format(d, 'yyyy-MM-dd');
      if (!byKey.has(key)) {
        const label = format(d, 'EEEE, MMMM d');
        const entry = { key, label, items: [] };
        byKey.set(key, entry);
        groups.push(entry);
      }
      byKey.get(key).items.push(s);
    }
    return groups;
  }

  if (loading) return <div style={styles.loading}>Loading dashboard...</div>;

  return (
    <div style={styles.page}>
      {/* Side by side above 900px — Past 7 Days is easy to miss stacked
          full-width underneath everything else; stacks back to one column
          on phone where there's no room for two. */}
      <style>{`
        /* Grid items default to min-width: auto, which refuses to shrink
           below the content's natural size — a long row of text inside
           just pushes the track (and the whole page) wider instead of
           wrapping or clipping. This is what actually caused the overflow,
           not the column count itself. */
        .db-two-col > div { min-width: 0; }
        @media (max-width: 900px) {
          .db-two-col { grid-template-columns: 1fr !important; }
        }
      `}</style>
      {/* Header */}
      <div style={styles.pageHeader}>
        <div style={styles.pageTitle}>Dashboard</div>
        <div style={styles.pageDate}>{format(today, 'EEEE, MMMM d, yyyy')}</div>
      </div>

      {error && (
        <div style={{ background: '#FBF1EF', border: '1px solid #B33A2E', borderRadius: 10, padding: '12px 16px', marginBottom: 16, color: '#B33A2E', fontSize: 13 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Couldn't load dashboard data</div>
          <div style={{ marginBottom: 8 }}>Check your internet connection, then tap Retry.</div>
          <button
            style={{ background: '#1F3A47', border: 'none', color: '#FFFFFF', borderRadius: 6, padding: '7px 12px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
            onClick={fetchDashboardData}>Retry</button>
        </div>
      )}

      {/* Alerts */}
      {eventPct > 80 && (
        <AlertBanner
          type="warning"
          message={`Special event hours at ${eventPct.toFixed(0)}% — only ${eventRemaining.toFixed(0)} hrs remaining before overage billing applies. Pre-approval required for any hours over 600.`}
        />
      )}
      {negativeReports >= 4 && (
        <AlertBanner
          type="danger"
          message={`${negativeReports} negative reports this month. 5 or more triggers a 10% invoice deduction. Review and respond immediately.`}
        />
      )}

      {/* GPS Summary Bar */}
      {todaySubmissions.length > 0 && (
        <div style={styles.gpsSummary}>
          <div style={styles.gpsSummaryTitle}>📍 Location Verification</div>
          <div style={styles.gpsSummaryRow}>
            <div style={styles.gpsChip}>
              <span style={{ color: '#2F7A4F', fontWeight: 800 }}>{verifiedCount}</span>
              <span style={{ color: '#5B6B72' }}> on-site</span>
            </div>
            {unverifiedCount > 0 && (
              <div style={styles.gpsChip}>
                <span style={{ color: '#96600A', fontWeight: 800 }}>{unverifiedCount}</span>
                <span style={{ color: '#5B6B72' }}> off-site</span>
              </div>
            )}
            {noGpsCount > 0 && (
              <div style={styles.gpsChip}>
                <span style={{ color: '#5B6B72', fontWeight: 800 }}>{noGpsCount}</span>
                <span style={{ color: '#5B6B72' }}> no GPS</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="db-two-col" style={styles.twoCol}>
      {/* Today's status by building */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>Today's Building Status</div>
        <div style={styles.buildingGrid}>
          {activeBuildings.map(b => {
            const done = cleanedToday.has(b.id);
            const expectedToday = isExpectedToday(b, today);
            const sub = todaySubmissions.find(s => s.building_id === b.id);
            const gpsBadge = sub ? getGpsBadge(sub) : null;
            return (
              <div key={b.id} style={{ ...styles.buildingRow, borderLeftColor: done ? '#2F7A4F' : '#E4E0D8' }}>
                <div style={{
                  ...styles.bid,
                  background: done ? '#2F7A4F' : '#F5F3EF',
                  color: done ? '#FFFFFF' : '#1A2B33',
                  border: done ? 'none' : '1px solid #E4E0D8',
                }}>{b.id}</div>
                <div style={styles.bInfo}>
                  <div style={styles.bName}>{b.name}</div>
                  <div style={styles.bWindow}>{b.window}</div>
                </div>
                <div style={styles.bStatus}>
                  {done ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={styles.doneChip}>
                        ✓ {sub ? sub.worker_name : 'Done'}
                      </div>
                      {gpsBadge && (
                        <div style={{
                          ...styles.gpsInlineBadge,
                          color: gpsBadge.color,
                          background: gpsBadge.bg,
                        }}>
                          {gpsBadge.icon} {gpsBadge.label}
                        </div>
                      )}
                    </div>
                  ) : expectedToday ? (
                    <div style={styles.pendingChip}>Pending</div>
                  ) : (
                    <div style={{ ...styles.pendingChip, color: '#5B6B72' }}>Not scheduled today</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Past 7 days, grouped by day — separate from the today-only stats above */}
      {recentSubmissions.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Past 7 Days</div>
          <div style={{ fontSize: 12, color: '#5B6B72', padding: '0 20px 8px' }}>
            Every check-in and check-out from the last week, most recent day first.
          </div>
          {groupByDay(recentSubmissions).map((day) => (
            <div key={day.key} style={{ marginBottom: 8 }}>
              <div style={styles.dayGroupLabel}>{day.label}</div>
              <div style={styles.subList}>
                {day.items.map(renderSubmissionRow)}
              </div>
            </div>
          ))}
        </div>
      )}
      </div>

      {/* Key metrics — kept below the day-to-day operational view (building
          status, recent activity) rather than at the very top, since that's
          what a manager actually wants to see first, especially on a phone
          where the top of the page is all that's visible without scrolling. */}
      <div style={styles.metricsGrid}>
        <MetricCard
          label="Monthly Revenue"
          value={`$${currentRevenue.toLocaleString()}`}
          sub="Active buildings only"
          color="#2F7A4F"
          icon="$"
        />
        <MetricCard
          label="Cleaned Today"
          value={`${cleanedToday.size} / ${expectedTodayBuildings.length}`}
          sub={`${pendingToday.length} still pending`}
          color={pendingToday.length === 0 ? '#2F7A4F' : '#96600A'}
          icon="✓"
        />
        <MetricCard
          label="Event Hours Used"
          value={`${specialEventHours.toFixed(1)} / ${SPECIAL_EVENTS_ALLOCATION}`}
          sub={`${eventRemaining.toFixed(0)} hrs remaining`}
          color={eventPct > 80 ? '#B33A2E' : eventPct > 60 ? '#96600A' : '#2F7A4F'}
          icon="★"
          showBar
          barPct={eventPct}
        />
        <MetricCard
          label="Negative Reports"
          value={negativeReports}
          sub={`This month · ${monthName}`}
          color={negativeReports >= 4 ? '#B33A2E' : negativeReports >= 3 ? '#96600A' : '#2F7A4F'}
          icon="⚠"
        />
      </div>
    </div>
  );
}

/* --- Helper Components --- */

function MetricCard({ label, value, sub, color, icon, showBar, barPct }) {
  return (
    <div style={styles.metric}>
      <div style={styles.metricIcon}>
        <span style={{ color, fontSize: 18, fontWeight: 800 }}>{icon}</span>
      </div>
      <div style={styles.metricLabel}>{label}</div>
      <div style={{ ...styles.metricValue, color }}>{value}</div>
      {showBar && (
        <div style={styles.bar}>
          <div style={{ ...styles.barFill, width: `${barPct}%`, background: color }} />
        </div>
      )}
      <div style={styles.metricSub}>{sub}</div>
    </div>
  );
}

function AlertBanner({ type, message }) {
  const colors = type === 'danger'
    ? { bg: '#FBF1EF', border: '#B33A2E', text: '#B33A2E' }
    // Amber banner text: #B5730A fails contrast at this size (same issue
    // already found and fixed on Command Central/Schedule), so #96600A is
    // used for the readable text while #B5730A stays the border/fill.
    : { bg: 'rgba(181,115,10,0.08)', border: '#B5730A', text: '#96600A' };
  return (
    <div style={{
      margin: '0 0 16px',
      padding: '12px 16px',
      background: colors.bg,
      border: `1px solid ${colors.border}`,
      borderRadius: 10,
      color: colors.text,
      fontSize: 13,
      lineHeight: 1.5,
    }}>
      {message}
    </div>
  );
}

/* --- Styles --- */

const styles = {
  page: { padding: 16, background: '#F5F3EF' },
  loading: { padding: 40, textAlign: 'center', color: '#5B6B72' },
  pageHeader: { marginBottom: 20 },
  pageTitle: { fontSize: 22, fontWeight: 800, color: '#1A2B33' },
  pageDate: { fontSize: 13, color: '#5B6B72', marginTop: 2 },

  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 12,
    marginBottom: 16,
  },
  metric: {
    background: '#FFFFFF',
    border: '1px solid #E4E0D8',
    borderRadius: 12,
    padding: 16,
  },
  metricIcon: { marginBottom: 8 },
  metricLabel: { fontSize: 11, color: '#5B6B72', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  metricValue: { fontSize: 24, fontWeight: 800, marginTop: 4 },
  metricSub: { fontSize: 12, color: '#5B6B72', marginTop: 4 },
  bar: { height: 4, background: '#E4E0D8', borderRadius: 2, marginTop: 8, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 2, transition: 'width 0.5s' },

  // GPS Summary
  gpsSummary: {
    background: '#FFFFFF',
    border: '1px solid #E4E0D8',
    borderRadius: 12,
    padding: '12px 16px',
    marginBottom: 16,
  },
  gpsSummaryTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#1A2B33',
    marginBottom: 8,
  },
  gpsSummaryRow: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
  },
  gpsChip: {
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },

  // Building status
  section: { marginBottom: 24 },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' },
  sectionTitle: { fontSize: 15, fontWeight: 700, color: '#1A2B33', marginBottom: 12 },
  dayGroupLabel: { fontSize: 11, fontWeight: 700, color: '#5B6B72', padding: '6px 12px 2px', textTransform: 'uppercase', letterSpacing: 0.4 },
  buildingGrid: { display: 'flex', flexDirection: 'column', gap: 6 },
  buildingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 12px',
    background: '#FFFFFF',
    borderRadius: 10,
    borderLeft: '3px solid',
  },
  bid: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: 13,
    flexShrink: 0,
  },
  bInfo: { flex: 1, minWidth: 0 },
  bName: { fontSize: 13, fontWeight: 600, color: '#1A2B33', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  bWindow: { fontSize: 11, color: '#5B6B72', marginTop: 1 },
  bStatus: { flexShrink: 0 },
  doneChip: {
    background: 'rgba(47,122,79,0.10)',
    color: '#2F7A4F',
    fontSize: 11,
    fontWeight: 700,
    padding: '3px 8px',
    borderRadius: 6,
    whiteSpace: 'nowrap',
  },
  pendingChip: {
    background: 'rgba(91,107,114,0.08)',
    color: '#5B6B72',
    fontSize: 11,
    fontWeight: 600,
    padding: '3px 8px',
    borderRadius: 6,
  },

  // GPS inline badge — only used in the expanded detail panel now, the
  // collapsed row just shows a small dot (see statusDot below).
  gpsInlineBadge: {
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 7px',
    borderRadius: 5,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  // Two small dots instead of two full text badges on the collapsed row —
  // full check-in/checkout detail is one tap away in the expanded panel,
  // so the row itself only needs an at-a-glance signal, not the full label.
  dotWrap: { display: 'flex', gap: 4, flexShrink: 0 },
  statusDot: { width: 8, height: 8, borderRadius: '50%', display: 'inline-block' },

  // Submissions list
  subList: { display: 'flex', flexDirection: 'column', gap: 1 },
  subRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '6px 12px',
    borderRadius: 8,
    transition: 'background 0.15s',
  },
  subBid: {
    width: 24,
    height: 24,
    borderRadius: 6,
    background: '#FFFFFF',
    border: '1px solid #E4E0D8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: 11,
    color: '#1A2B33',
    flexShrink: 0,
  },
  // minWidth: 0 overrides a flex item's default of never shrinking below its
  // own content size — without it, a long name next to the non-wrapping
  // time range forces the whole row (and the page) wider than the screen
  // instead of ellipsizing.
  subName: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, fontWeight: 600, color: '#1A2B33' },
  subTime: { fontSize: 12, color: '#5B6B72', whiteSpace: 'nowrap', flexShrink: 0 },

  // GPS expanded detail
  gpsDetail: {
    margin: '0 0 8px 0',
    padding: '14px 16px',
    background: '#F5F3EF',
    border: '1px solid #E4E0D8',
    borderRadius: 10,
    marginLeft: 40,
  },
  gpsDetailDivider: { height: 1, background: '#E4E0D8', margin: '12px 0' },
  gpsDetailGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px 20px',
  },
  gpsDetailItem: {},
  gpsDetailLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: '#5B6B72',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  mapBtn: {
    marginTop: 12,
    width: '100%',
    padding: '9px 0',
    background: '#FFFFFF',
    border: '1px solid #E4E0D8',
    borderRadius: 8,
    color: '#1A2B33',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'center',
  },
};
